import { FormPayload, FormPayloadSyonet } from "@/app/interface/components";
import { SYONET_ENABLED } from "@/app/config/integrations";
import { normalizeModelName } from "./handleSubmit";
import { getPersistedUtmParams } from "@/app/utils/utm";
import { sendToToyotaLeadCenter } from "./toyotaLeadCenterIntegration";
import { sendToAlan } from "./alanIntegration";

const SYONET_DEALER_IDS: { [key: string]: string } = {
	"EPIA SUL": "24",
	SIA: "25",
	TAGUATINGA: "26",
};

// Função auxiliar para determinar o eventType baseado na página
function getEventTypeFromPathname(): string {
	if (typeof window === "undefined") return "NOVOS WEB";

	const pathname = window.location.pathname.toLowerCase();

	if (pathname.includes("/venda-direta")) {
		return "VENDA DIRETA WEB";
	} else if (pathname.includes("/consorcio")) {
		return "CONSORCIO WEB";
	} else if (pathname.includes("/assinatura")) {
		return "ASSINATURA WEB";
	} else if (
		pathname.includes("/pos-venda") ||
		pathname.includes("/servicos")
	) {
		return "SERVICOS WEB";
	}

	return "NOVOS WEB";
}

export async function sendToSyonet(
	formData: Omit<FormPayload, "registrationNumber"> & {
		registrationNumber?: string;
	},
	brand: string,
	options?: {
		skipToyotaLeadCenter?: boolean;
		skipWhatsAppRedirect?: boolean;
		enableSyonet?: boolean;
	},
) {
	try {
		// Cast to any to accept frontend-specific fields (nome_loja, categoria, phone)
		const fd: any = formData as any;
		// Accept either 'nome_loja' (frontend) or 'dealer' (legacy) as source for the dealer name
		const dealerName = (fd.nome_loja || fd.dealer || "")
			.toString()
			.toUpperCase();
		const companyId = SYONET_DEALER_IDS[dealerName] || "24";
		let modelValue = "Não informado";
		if (formData.model) {
			const modelStr = formData.model.toString();
			if (
				modelStr &&
				modelStr !== "0" &&
				!["NaN", "undefined", "null"].includes(modelStr)
			) {
				modelValue = normalizeModelName(modelStr);
			}
		}

		// Recupera UTMs persistidos, se não houver, tenta pegar da URL atual
		let utmParamsString = getPersistedUtmParams();
		if (!utmParamsString && typeof window !== "undefined") {
			utmParamsString = window.location.search;
		}

		const urlParams = new URLSearchParams(utmParamsString);
		const utmParams = {
			utmcsr: urlParams.get("utm_source") || "",
			utmcmd: urlParams.get("utm_medium") || "Orgânico",
			utmccn: urlParams.get("utm_campaign") || "",
			utmcct: urlParams.get("utm_content") || "",
			utmctm: urlParams.get("utm_term") || "",
		};

		// Determina o eventType: prioriza o valor vindo do formulário, senão lê a URL
		const fdEventType = Array.isArray(fd.eventType)
			? fd.eventType[0]
			: fd.eventType;
		const eventType = fdEventType || getEventTypeFromPathname();

		const syonetPayload: FormPayloadSyonet = {
			formPayload: {
				name: formData.name,
				email: formData.email || "",
				...(formData.registrationNumber !== undefined
					? { document: formData.registrationNumber }
					: {}),
				phone: fd.phone || fd.cellPhone || "",
				companyId: companyId,
				eventType: eventType,
				model: modelValue,
				hubUtmz: utmParams,
				influencer: "",
				page: formData.page || "",
				comment: (fd.comment || "").toString(),
				...(fd.media ? { media: fd.media.toString() } : {}),
				isChat: false,
			},
			brand: brand.toUpperCase(),
			isChat: false,
		};

		if (!options?.skipToyotaLeadCenter) {
			sendToToyotaLeadCenter({
				name: formData.name,
				email: formData.email || "",
				phone: fd.phone || fd.cellPhone || "",
				document: formData.registrationNumber,
				brand: brand.toUpperCase(),
				model: modelValue,
				page: formData.page,
				comment: (fd.comment || "").toString(),
				dealer_group: undefined,  // será preenchido com KYOTO automaticamente
				companyId: companyId,
				eventType: eventType,
				utm_source: utmParams.utmcsr || undefined,
				utm_medium: utmParams.utmcmd || undefined,
				utm_campaign: utmParams.utmccn || undefined,
				utm_content: utmParams.utmcct || undefined,
				utm_term: utmParams.utmctm || undefined,
				isChat: false,
				source: "form",
			}).catch(() => {});
		}

		const alanOk = await sendToAlan(
			{ ...formData, model: modelValue },
			{ skipWhatsAppRedirect: options?.skipWhatsAppRedirect },
		);

		if (!alanOk && options?.enableSyonet) {
			console.warn("Falha no Alan; prosseguindo com o envio ao Syonet.");
		}

		if (!SYONET_ENABLED && !options?.enableSyonet) {
			return alanOk;
		}

		// Chama a API route local que faz a comunicação segura com o backend
		const response = await fetch("/api/syonet", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(syonetPayload),
		});
		const responseText = await response.text();

		let responseData;
		try {
			responseData = responseText ? JSON.parse(responseText) : {};
		} catch (parseError) {
			console.error("Erro ao fazer parse da resposta:", parseError);
			responseData = { message: responseText };
		}

		if (!response.ok) {
			console.error("Erro ao enviar para Syonet:", {
				status: response.status,
				statusText: response.statusText,
				error: responseData,
			});
			throw new Error(
				`Erro ao enviar para Syonet: ${JSON.stringify(responseData)}`,
			);
		}

		return options?.enableSyonet ? true : alanOk;
	} catch (error) {
		console.error("Erro na integração com SYONET:", {
			message:
				error instanceof Error ? error.message : "Erro desconhecido",
			stack: error instanceof Error ? error.stack : undefined,
		});
		return false;
	}
}

export async function sendToSyonetWhatsapp(
	name: string,
	email: string,
	document: string,
	phone: string,
	dealer: string,
	model: string,
	utmParams?: string,
) {
	try {
		if (!SYONET_ENABLED) {
			return true;
		}

		const dealerName = dealer?.toUpperCase() || "";
		const companyId = SYONET_DEALER_IDS[dealerName] || "35";

		let modelValue = "Não informado";
		if (model) {
			const modelStr = model.toString();
			if (
				modelStr &&
				modelStr !== "0" &&
				!["NaN", "undefined", "null"].includes(modelStr)
			) {
				modelValue = normalizeModelName(modelStr);
			}
		}

		let hubUtmz = {
			utmcsr: "",
			utmcmd: "",
			utmccn: "",
			utmcct: "",
			utmctm: "",
		};

		if (utmParams) {
			const urlParams = new URLSearchParams(utmParams);
			hubUtmz = {
				utmcsr: urlParams.get("utm_source") || "",
				utmcmd: urlParams.get("utm_medium") || "Orgânico",
				utmccn: urlParams.get("utm_campaign") || "",
				utmcct: urlParams.get("utm_content") || "",
				utmctm: urlParams.get("utm_term") || "",
			};
		}

		const syonetPayload = {
			formPayload: {
				name: name,
				email: email,
				document: document,
				phone: phone,
				companyId: companyId,
				eventType: getEventTypeFromPathname(),
				model: modelValue,
				hubUtmz: hubUtmz,
				influencer: "whatsapp-site",
			},
			brand: "TOYOTA",
		};

		// Logs de debug para PRD - WhatsApp
		console.log("[SYONET WHATSAPP DEBUG] Payload preparado:", {
			companyId,
			modelValue,
			eventType: "NOVOS WEB",
			payloadSize: JSON.stringify(syonetPayload).length,
		});

		// Chama a API route local que faz a comunicação segura com o backend
		// A API route já adiciona a API_KEY e usa BACKEND_API_URL configurada no Azure
		console.log("[SYONET WHATSAPP DEBUG] Enviando para /api/syonet...");
		const startTime = Date.now();
		const response = await fetch("/api/syonet", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(syonetPayload),
		});
		const responseText = await response.text();
		let responseData;
		try {
			responseData = responseText ? JSON.parse(responseText) : {};
		} catch (parseError) {
			console.error(
				"Erro ao fazer parse da resposta SYONET WhatsApp:",
				parseError,
			);
			responseData = { message: responseText };
		}

		if (!response.ok) {
			console.error("[SYONET WHATSAPP DEBUG] Erro HTTP detalhado:", {
				status: response.status,
				statusText: response.statusText,
				responseHeaders: Object.fromEntries(response.headers.entries()),
				errorData: responseData,
				payloadSent: syonetPayload,
			});
			throw new Error(
				`Erro ao enviar para SYONET WhatsApp: ${JSON.stringify(
					responseData,
				)}`,
			);
		}

		console.log("[SYONET WHATSAPP DEBUG] Sucesso! Resposta:", responseData);
		return true;
	} catch (error) {
		console.error(
			"[SYONET DEBUG] Erro na integração sendToSyonetWhatsapp:",
			{
				errorType:
					error instanceof Error
						? error.constructor.name
						: typeof error,
				message:
					error instanceof Error
						? error.message
						: "Erro desconhecido",
				stack: error instanceof Error ? error.stack : undefined,
				userAgent:
					typeof window !== "undefined"
						? window.navigator.userAgent
						: "server",
				url:
					typeof window !== "undefined"
						? window.location.href
						: "server",
				timestamp: new Date().toISOString(),
			},
		);
		return false;
	}
}
